import React, { useState } from 'react';
import { Clock, Phone, Mail, MapPin, Users, CalendarDays, Send, Loader2 } from 'lucide-react';
import { restaurantInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReservationPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    guests: '',
    message: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useLanguage();

  React.useEffect(() => {
    document.title = 'Réservation - Ya Habibi! Restaurant Libanais Genève | Book a Table';
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/reservations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error('Erreur serveur');
      toast.success(t('reservation', 'successMsg'));
      setFormData({ name: '', email: '', phone: '', date: '', time: '', guests: '', message: '' });
    } catch {
      toast.error(t('reservation', 'errorMsg'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const timeSlots = [];
  for (let h = 12; h <= 14; h++) {
    for (let m = 0; m < 60; m += 15) {
      if (h === 14 && m > 0) break;
      timeSlots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
    }
  }
  for (let h = 18; h <= 22; h++) {
    const startM = h === 18 ? 30 : 0;
    for (let m = startM; m < 60; m += 15) {
      if (h === 22 && m > 30) break;
      timeSlots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
    }
  }

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="min-h-screen pt-24" data-testid="reservation-page">
      <section className="bg-[#F5E6D3] py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#0f172a] mb-4">
            {t('reservation', 'title')}
          </h1>
          <p className="text-lg text-[#0f172a]/80 max-w-2xl mx-auto">
            {t('reservation', 'subtitle')}
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="bg-[#f8fafc] rounded-2xl p-6 md:p-10" data-testid="reservation-form">
                <h2 className="font-serif text-2xl font-bold text-[#0f172a] mb-8 flex items-center gap-3">
                  <CalendarDays size={28} className="text-[#D4A853]" />
                  {t('reservation', 'formTitle')}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[#0f172a] font-medium mb-2">{t('reservation', 'fullName')}</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5E6D3] focus:border-transparent outline-none transition-all bg-white" data-testid="reservation-name-input" />
                  </div>
                  <div>
                    <label className="block text-[#0f172a] font-medium mb-2">{t('reservation', 'email')}</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5E6D3] focus:border-transparent outline-none transition-all bg-white" data-testid="reservation-email-input" />
                  </div>
                  <div>
                    <label className="block text-[#0f172a] font-medium mb-2">{t('reservation', 'phone')}</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5E6D3] focus:border-transparent outline-none transition-all bg-white" placeholder="+41 XX XXX XX XX" data-testid="reservation-phone-input" />
                  </div>
                  <div>
                    <label className="block text-[#0f172a] font-medium mb-2">{t('reservation', 'guests')}</label>
                    <select name="guests" value={formData.guests} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5E6D3] focus:border-transparent outline-none transition-all bg-white" data-testid="reservation-guests-select">
                      <option value="">{t('reservation', 'select')}</option>
                      {[1,2,3,4,5,6,7,8,9,10].map(n => (
                        <option key={n} value={n}>{n} {n === 1 ? t('reservation', 'person') : t('reservation', 'persons')}</option>
                      ))}
                      <option value="10+">{t('reservation', 'moreThan10')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[#0f172a] font-medium mb-2">{t('reservation', 'date')}</label>
                    <input type="date" name="date" value={formData.date} onChange={handleChange} required min={today} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5E6D3] focus:border-transparent outline-none transition-all bg-white" data-testid="reservation-date-input" />
                  </div>
                  <div>
                    <label className="block text-[#0f172a] font-medium mb-2">{t('reservation', 'time')}</label>
                    <select name="time" value={formData.time} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5E6D3] focus:border-transparent outline-none transition-all bg-white" data-testid="reservation-time-select">
                      <option value="">{t('reservation', 'select')}</option>
                      {timeSlots.map(ts => (
                        <option key={ts} value={ts}>{ts}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-6">
                  <label className="block text-[#0f172a] font-medium mb-2">{t('reservation', 'specialRequests')}</label>
                  <textarea name="message" value={formData.message} onChange={handleChange} rows="3" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5E6D3] focus:border-transparent outline-none transition-all resize-none bg-white" placeholder={t('reservation', 'placeholder')} data-testid="reservation-message-input" />
                </div>

                <button type="submit" disabled={isLoading} className="w-full mt-8 bg-[#0f172a] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#1e293b] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 text-lg" data-testid="reservation-submit-button">
                  {isLoading ? <Loader2 size={22} className="animate-spin" /> : <Send size={22} />}
                  {isLoading ? t('reservation', 'sending') : t('reservation', 'submit')}
                </button>

                <p className="text-sm text-[#64748b] mt-4 text-center">
                  {t('reservation', 'emailConfirm')}
                </p>
              </form>
            </div>

            <div className="lg:col-span-1">
              <div className="bg-[#f8fafc] rounded-2xl p-8 sticky top-32">
                <h3 className="font-serif text-xl font-bold text-[#0f172a] mb-6">
                  {t('reservation', 'practicalInfo')}
                </h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-[#0f172a] mb-2 flex items-center gap-2">
                      <Clock size={18} className="text-[#D4A853]" />
                      {t('reservation', 'hours')}
                    </h4>
                    <div className="text-[#64748b] space-y-1 ml-6">
                      <p className="text-green-600 font-medium">{t('reservation', 'open7')}</p>
                      <p className="mt-2">{restaurantInfo.hours.weekdaysLabel}</p>
                      <p className="text-sm">{restaurantInfo.hours.weekdays}</p>
                      <p className="mt-2">{restaurantInfo.hours.weekendLabel}</p>
                      <p className="text-sm">{restaurantInfo.hours.weekend}</p>
                    </div>
                  </div>
                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="font-semibold text-[#0f172a] mb-3 flex items-center gap-2">
                      <MapPin size={18} className="text-[#D4A853]" />
                      {t('reservation', 'address')}
                    </h4>
                    <p className="text-[#64748b] ml-6">{restaurantInfo.address}</p>
                  </div>
                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="font-semibold text-[#0f172a] mb-2 flex items-center gap-2">
                      <Users size={18} className="text-[#D4A853]" />
                      {t('reservation', 'groups')}
                    </h4>
                    <p className="text-[#64748b] text-sm ml-6">{t('reservation', 'groupsText')}</p>
                    <div className="ml-6 mt-2 space-y-1">
                      <div className="flex items-center gap-2">
                        <Phone size={16} className="text-[#D4A853]" />
                        <a href={`tel:${restaurantInfo.phone}`} className="text-[#0f172a] font-medium hover:text-[#D4A853] transition-colors">{restaurantInfo.phone}</a>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail size={16} className="text-[#D4A853]" />
                        <a href={`mailto:${restaurantInfo.email}`} className="text-[#D4A853] hover:text-[#8B4513] transition-colors">{restaurantInfo.email}</a>
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="font-semibold text-[#0f172a] mb-2">{t('reservation', 'cancellation')}</h4>
                    <p className="text-[#64748b] text-sm">{t('reservation', 'cancellationText')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReservationPage;
